import { Modal, Form, Input, Select, App, Tabs, Divider, Switch, Card, Button } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { hooks, apis, models } from '@vote4dance/judging-sdk';
import { Localization, _T } from '../../../../../base-react/src/Localization';
var Api = apis.Api;
var Stream = hooks.streams.Stream;
var TeamItem = models.teams.TeamItem;
var TeamSource = models.teams.TeamSource;
var ClassesSource = models.classes.ClassesSource;
var ClassTeamSource = models.teams.ClassTeamSource;
var AcroLevelSource = models.judges.AcroLevelSource;
import { DeleteOutlined, PrinterOutlined, UserOutlined } from '@ant-design/icons';
import { useFederationSettings } from '../../../../../base-react/hooks/Federation';
import { Link, useParams } from 'react-router-dom';
import { ButtonConfirm } from '../../../../../base-react/components/ButtonConfirm';
import { useCompetition } from '../../../../../base-react/hooks/Competition';
import ImageUpload from '../../../../../base-react/components/ImageUpload';
const { Option } = Select;
const extractTeamImageId = (result) => {
    if (result == null) {
        return undefined;
    }
    if (typeof result === 'string' || typeof result === 'number') {
        return String(result);
    }
    if (result.team_image_id != null) {
        return String(result.team_image_id);
    }
    if (result.image_id != null) {
        return String(result.image_id);
    }
    return undefined;
};
export const TeamEdit = (props) => {
    const { competition_id, teamItem, classItem, visible, onClose, onDuplicate, disabled } = props;
    const [classes, loadingClasses] = Stream(ClassesSource(competition_id));
    const [acroLevels, loadingAcroLevels] = Stream(AcroLevelSource());
    const [inputCompetition] = useCompetition(competition_id);
    const [parentTeams, loadingParentTeams] = Stream((classItem && classItem.class_role === null && parseInt(classItem.class_group_id) > 0) ? ClassTeamSource(classItem.class_group_id) : undefined);
    const { notification } = App.useApp();
    const [isLoading, setIsLoading] = useState(false);
    const [form] = Form.useForm();
    const teamImageId = Form.useWatch('team_image_id', form);
    const { usingAcrobaticLevels } = useFederationSettings(competition_id);
    const { team_id } = useParams();
    const teamURL = TeamSource(competition_id).url;
    const inputRef = useRef(null);
    const [pendingTeamImage, setPendingTeamImage] = useState(undefined);
    const focusStart = () => {
        // Ensure field is in view, then focus the input
        form.scrollToField('team_start_number', { behavior: 'smooth', block: 'center' });
        // Delay to next frame so the element exists after mount/updates
        requestAnimationFrame(() => { var _a; return (_a = inputRef.current) === null || _a === void 0 ? void 0 : _a.focus({ cursor: 'end' }); });
    };
    useEffect(() => {
        if (!visible) {
            return;
        }
        form.resetFields();
        form.setFieldsValue(getDefaultValue(teamItem));
        focusStart();
        setPendingTeamImage(undefined);
    }, [visible, teamItem, classItem]); // eslint-disable-line react-hooks/exhaustive-deps
    useEffect(() => {
        const pathname = window.location.pathname;
        if (visible && !team_id) {
            const separator = pathname.at(-1) == '/' ? '' : '/';
            const newLocation = pathname + separator + (teamItem ? teamItem.team_id : 'new');
            window.history.replaceState(null, '', newLocation);
        }
        if (visible)
            focusStart();
    }, [visible]); // eslint-disable-line react-hooks/exhaustive-deps
    const getDefaultValue = (item) => ({
        ...item,
        team_starting_in: item ? item.class_id : (classItem && classItem.class_id ? [classItem.class_id] : []),
        team_placement: item ? item.team_placement : '',
        team_image_id: (item === null || item === void 0 ? void 0 : item.team_image_id) != null ? String(item.team_image_id) : null
    });
    const afterClose = () => {
        const pathname = window.location.pathname;
        const newLocation = `${pathname}/..`;
        window.history.replaceState(null, '', newLocation);
    };
    const onFinish = async (teamItem, fieldsValue) => {
        setIsLoading(true);
        let errorsFound = 0;
        const { team_image_id: _teamImageId, ...fieldsWithoutImage } = fieldsValue;
        const selectedClass = fieldsValue['team_starting_in'];
        const updateItem = {
            ...teamItem,
            ...fieldsWithoutImage,
            team_cancelled: fieldsValue['team_disqualified'] > 0 ? 5 : fieldsValue['team_cancelled'],
            class_id: selectedClass
        };
        const applyTeamImage = async (targetTeamId) => {
            var _a;
            if (pendingTeamImage === undefined || targetTeamId == null) {
                return;
            }
            try {
                const imageResult = await Api.call(`${teamURL}/${targetTeamId}`, 'set-picture', { picture: pendingTeamImage });
                const imageId = extractTeamImageId(imageResult);
                form.setFieldValue('team_image_id', imageId !== null && imageId !== void 0 ? imageId : null);
            }
            catch (err) {
                notification.error({
                    title: ((_a = err.response) === null || _a === void 0 ? void 0 : _a.message) ? _T(err.response.message) : err.message
                });
            }
        };
        if (teamItem === null || teamItem === void 0 ? void 0 : teamItem.team_id) {
            await Api.update(`${teamURL}/${teamItem === null || teamItem === void 0 ? void 0 : teamItem.team_id}`, updateItem)
                .catch(err => {
                var _a;
                errorsFound++;
                notification.error({ title: ((_a = err.response) === null || _a === void 0 ? void 0 : _a.message) ? _T(err.response.message) : err.name });
            });
            if (!errorsFound) {
                await applyTeamImage(teamItem.team_id);
            }
        }
        else {
            for (const class_id of Array.isArray(selectedClass) ? selectedClass : [selectedClass]) {
                updateItem.class_id = class_id;
                const createdTeam = await Api.create(teamURL, updateItem)
                    .catch(err => {
                    errorsFound++;
                    notification.error({ title: err.name });
                    return undefined;
                });
                if (createdTeam === null || createdTeam === void 0 ? void 0 : createdTeam.team_id) {
                    await applyTeamImage(createdTeam.team_id);
                }
            }
        }
        setIsLoading(false);
        if (!errorsFound) {
            form.resetFields();
            setPendingTeamImage(undefined);
            onClose();
        }
    };
    const handleCancel = () => {
        form.resetFields();
        setPendingTeamImage(undefined);
        onClose();
    };
    const handleDuplicate = () => {
        if (teamItem) {
            teamItem && onDuplicate && onDuplicate(teamItem);
            teamItem.team_id = undefined;
        }
        focusStart();
    };
    const handleDelete = async (done) => {
        if (teamItem === null || teamItem === void 0 ? void 0 : teamItem.team_id) {
            await Api.destroy(`${teamURL}/${teamItem.team_id}`)
                .then(() => {
                notification.success({
                    title: _T('common.delete.success', { name: _T('team.title').toLowerCase() })
                });
            })
                .catch((err) => {
                notification.error({
                    title: err.response.message ? _T(err.response.message) : err.message
                });
            });
        }
        handleCancel();
        done();
    };
    const formName = 'team_edit';
    return (React.createElement(Modal, { title: _T('team.details.title'), open: visible, confirmLoading: isLoading, destroyOnHidden: true, afterClose: afterClose, afterOpenChange: (open) => { if (open)
            focusStart(); }, onCancel: handleCancel, footer: [
            onDuplicate && teamItem && teamItem.team_id &&
                React.createElement(Button, { key: "duplicate", type: "dashed", onClick: handleDuplicate, disabled: isLoading }, _T('common.copy')),
            (teamItem === null || teamItem === void 0 ? void 0 : teamItem.team_id) && React.createElement(ButtonConfirm, { key: "delete", title: _T('common.delete.title'), description: _T('common.delete.content', { name: _T('team.title').toLowerCase() }), onConfirm: handleDelete, buttonProps: {
                    danger: true,
                    disabled: disabled,
                    icon: React.createElement(DeleteOutlined, null)
                } }),
            (teamItem === null || teamItem === void 0 ? void 0 : teamItem.team_id) && React.createElement(Button, { key: "print", icon: React.createElement(Link, { target: "_blank", to: `/print/${competition_id}/startnumber/${teamItem.team_start_number}` },
                    React.createElement(PrinterOutlined, null)) }),
            React.createElement(Button, { key: "cancel", onClick: handleCancel, disabled: isLoading }, _T('common.cancel.title')),
            React.createElement(Button, { key: "submit", type: "primary", form: formName, htmlType: 'submit', disabled: isLoading, loading: isLoading }, _T('common.save'))
        ] },
        React.createElement(Form, { name: formName, labelCol: { span: 8 }, wrapperCol: { span: 16 }, form: form, disabled: isLoading || disabled, onFinish: (values) => onFinish(teamItem, values), initialValues: getDefaultValue(teamItem) },
            React.createElement(Form.Item, { label: _T('team.details.team_start_number.label'), extra: _T('team.details.team_start_number.hint'), rules: [{ required: true, message: _T('common.required') }], name: "team_start_number" },
                React.createElement(Input, { allowClear: true, ref: inputRef, autoComplete: "off" })),
            React.createElement(Form.Item, { label: _T('team.details.team_starting_in.label'), name: "team_starting_in", rules: [{ required: true, message: _T('team.details.team_starting_in.rule') }] },
                React.createElement(Select, { mode: teamItem ? undefined : "multiple", allowClear: true, loading: loadingClasses, placeholder: _T('team.details.team_starting_in.placeholder') }, classes && classes.map((a, index) => (React.createElement(Option, { value: a.class_id, key: index }, a.class_label ? a.class_label : a.class_name))))),
            React.createElement(Form.Item, { label: _T('team.details.team_placement.label'), name: "team_placement" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Form.Item, { label: _T('team.details.team_sum.label'), name: "team_sum" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Tabs, { items: [UserOutlined, UserOutlined, UserOutlined].map((Icon, i) => {
                    const id = String(i + 1);
                    return {
                        label: (React.createElement("span", null,
                            React.createElement(Icon, null),
                            _T('team.details.team_name.tab', { count: id }))),
                        key: id,
                        children: React.createElement(Card, null, ['team_name', 'team_org', 'team_license', 'team_person_id', 'team_year'].map((value, i) => (React.createElement(Form.Item, { key: i, label: _T(`team.details.${value}.label`), name: `${value}_${id}` },
                            React.createElement(Input, { allowClear: true })))))
                    };
                }) }),
            React.createElement(Divider, null),
            React.createElement(Form.Item, { label: _T('team.details.team_org_full.label'), extra: _T('team.details.team_org.extra'), name: "team_org" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Form.Item, { label: _T('team.details.team_org_full.label'), extra: _T('team.details.team_org_full.extra'), name: "team_org_full" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Form.Item, { name: "team_country", label: _T('team.details.team_country.label') },
                React.createElement(Select, { allowClear: true, showSearch: true, optionFilterProp: "label", placeholder: _T('team.details.team_country.placeholder'), options: Object.keys(Localization.getCountryNames()).map((code) => ({
                        value: code,
                        label: Localization.getCountryName(code),
                    })) })),
            React.createElement(Form.Item, { name: "team_image_id", hidden: true },
                React.createElement(Input, null)),
            React.createElement(Form.Item, { label: _T('common.image') },
                React.createElement(ImageUpload, { value: teamImageId != null && teamImageId !== ''
                        ? String(teamImageId)
                        : undefined, disabled: isLoading || disabled, onChange: (imageData) => {
                        setPendingTeamImage(imageData);
                        if (imageData == null) {
                            form.setFieldValue('team_image_id', null);
                        }
                    } })),
            React.createElement(Form.Item, { label: _T('team.details.team_show_name.label'), name: "team_show_name" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Divider, null),
            React.createElement(Form.Item, { label: _T('team.details.team_class.label'), extra: _T('team.details.team_class.extra'), name: "team_class" },
                React.createElement(Select, { allowClear: true, loading: loadingClasses, placeholder: _T('team.details.team_class.placeholder') }, classes && classes.map((a, index) => (React.createElement(Option, { value: a.class_name, key: index }, a.class_label))))),
            React.createElement(Form.Item, { label: _T('team.details.team_starting_round.label'), extra: _T('team.details.team_starting_round.extra'), name: "team_starting_round" },
                React.createElement(Input, { allowClear: true })),
            React.createElement(Divider, null),
            ['team_warning', 'team_note', 'team_url'].map((value, i) => (React.createElement(Form.Item, { label: _T(`team.details.${value}.label`), name: value, key: i },
                React.createElement(Input, { allowClear: true })))),
            React.createElement(Divider, null),
            React.createElement(Form.Item, { label: _T('team.details.team_cancelled.label'), name: "team_cancelled" },
                React.createElement(Select, null, [0, 1, 2, 3, 4, 5].map((number) => (React.createElement(Option, { value: number, key: number }, _T('team.details.team_cancelled.reason.' + number)))))),
            React.createElement(Form.Item, { label: _T('team.details.team_disqualified.label'), name: "team_disqualified", valuePropName: "checked" },
                React.createElement(Switch, null)),
            React.createElement(Form.Item, { label: _T('team.details.team_checked_in.label'), name: "team_checked_in", valuePropName: "checked" },
                React.createElement(Switch, null)),
            usingAcrobaticLevels &&
                React.createElement(Form.Item, { label: _T('team.details.acro_id.label'), name: "acro_id" },
                    React.createElement(Select, { allowClear: true, placeholder: _T('team.details.acro_id.placeholder'), loading: loadingAcroLevels }, acroLevels && acroLevels.map((acro, index) => (React.createElement(Option, { value: acro.acro_id, key: index }, acro.acro_name))))),
            classItem && classItem.class_role === null && parseInt(classItem.class_group_id) > 0 &&
                React.createElement(Form.Item, { label: _T('team.details.team_parent_id.label'), name: "team_parent_id" },
                    React.createElement(Select, { allowClear: true, placeholder: _T('team.details.team_parent_id.placeholder'), loading: loadingParentTeams }, parentTeams && parentTeams.map((team, index) => (React.createElement(Option, { value: team.team_id, key: index }, TeamItem.getFullName(team)))))))));
};
